import {
  FlowStepResult,
  FlowStepResultTypenames,
} from '@varo/graphql-schema-types';
import { usePathname } from 'expo-router';
import { getIntentBaseOnboardingScreenName } from 'features/onboarding/utils/mapToERoute';
import { useEffect } from 'react';
import currentFlowStep from 'services/graphql/queries/onboarding/currentFlowStep.query';
import { redirectToHomePage } from 'shared/helpers/backNavigation.helpers';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';
import { useQuery } from 'urql';

const useGetCurrentFlowStep = () => {
  const pathname = usePathname();
  const { segmentClient } = useAnalyticsConfiguration();

  const [{ data, error, fetching: isCurrentFlowStepLoading }] = useQuery<{
    currentFlowStep: FlowStepResult;
  }>({
    query: currentFlowStep,
    requestPolicy: 'network-only',
  });

  const currentFlowStepData =
    data?.currentFlowStep?.__typename === FlowStepResultTypenames.FlowStep
      ? data.currentFlowStep
      : undefined;

  const isFlowCompleted =
    data?.currentFlowStep?.__typename === FlowStepResultTypenames.FlowCompleted;

  const flowStepErrorMessage =
    data?.currentFlowStep?.__typename === FlowStepResultTypenames.FlowStepError
      ? data.currentFlowStep.message
      : undefined;

  const hasFlowStepError = Boolean(flowStepErrorMessage) || isNotNil(error);

  useEffect(() => {
    if (isFlowCompleted || hasFlowStepError) {
      if (hasFlowStepError) {
        const stackTrace = error?.graphQLErrors.flatMap(
          (err) => err.stack,
        )?.[0];

        void segmentClient.logEvent('Onboarding Flow Error Encountered', {
          category: 'onboarding',
          subcategory: 'intent_based_onboarding',
          error_description: flowStepErrorMessage || stackTrace,
          step_name: getIntentBaseOnboardingScreenName(pathname),
        });
      }
      redirectToHomePage();
    }
  }, [
    isFlowCompleted,
    hasFlowStepError,
    error?.graphQLErrors,
    segmentClient,
    flowStepErrorMessage,
    pathname,
  ]);

  const flowStepId = currentFlowStepData?.id;

  return {
    flowStepId,
    isCurrentFlowStepLoading: isCurrentFlowStepLoading || isFlowCompleted,
    currentFlowStepData,
  };
};

export default useGetCurrentFlowStep;
